import { IAction } from './';
import { ThunkAction } from 'typed/utils/redux';
import { HTTPStatusCode, RequestStatus } from 'typed/api';
import { APIMethod } from 'typed/api/methods';
import { failedAPIResponse, voidSession } from '../app';
import * as Sentry from '@sentry/react';

const globalErrorAction = (error: any, notifyUser = true): any => (dispatch: any) => {
    if (error.status === HTTPStatusCode.UNAUTHORIZED) {
        dispatch(voidSession(true)); // this stuff is handled by the application core, we don't need to describe this in specific action types
    }

    if (!error.timeout && notifyUser) {
        dispatch(failedAPIResponse()); // this stuff is handled by the application core, we don't need to describe this in specific action types
    }

    if (!DEBUG && !error.timeout) {
        Sentry.captureException(error);
    }
};

type NetworkActionPayload<P, R> = P & (
    { status: typeof RequestStatus.UNSYNCED; } |
    { status: typeof RequestStatus.PENDING; } |
    { status: typeof RequestStatus.SUCCEEDED; response: R; } |
    { status: typeof RequestStatus.FAILED; error: any; }
);

export interface NetworkAction<T extends string, P, R, AP = NetworkActionPayload<P, R>> extends ThunkAction<Promise<R | undefined>, void, void, IAction<T, AP>> {
    type?: T;
    method?: APIMethod<R>;
}

export default function networkAction<T extends string, R = void>(type: T, method: APIMethod<R>, ...args: any[]): NetworkAction<T, void, R>;
export default function networkAction<T extends string, P, R = void>(type: T, params: P, method: APIMethod<R>, ...args: any[]): NetworkAction<T, P, R>;
export default function networkAction<T extends string, P, R>(type: T, methodOrParams: APIMethod<R> | P, ...args: any[]): NetworkAction<T, P, R> {
    const
        noParams: boolean = typeof methodOrParams === 'function',
        method: APIMethod<R> = noParams ? methodOrParams : args[0],
        params: P = noParams ? null as unknown as P : methodOrParams as P,
        margs: any[] = noParams ? args : args.slice(1),

        result: NetworkAction<T, P, R> = async(dispatch) => {
            dispatch({ type, payload: { status: RequestStatus.PENDING, ...params } });

            try {
                return dispatch({ type, payload: { status: RequestStatus.SUCCEEDED, response: await method(...margs), ...params } }).payload.response;
            }
            catch (error) {
                dispatch(globalErrorAction(error, !type.includes('WORKSHOP')));
                dispatch({ type, payload: { status: RequestStatus.FAILED, error, ...params } });

                // all components subscribing for promises have to have some error checking code, otherwise uncaught error will break execution
                // TypeScript compiler doesn't notify about probably missing 'catch' (and I doubt it's possible at all)
                // but it does notify if some code tries to use a variable which contains 'undefined' instead of an object
                return undefined;
            }
        };

    result.type = type;
    result.method = method;
    return result;
}
